import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';
import Avatar from '../../../components/ui/Avatar';
import MultiplayerInviteModal from '../../../modals/MultiplayerInviteModal';
import MultiplayerResults from '../../../modals/MultiplayerResults';

// 4×4 grid: 15 identical + 1 different symbol. Find the odd one. 10 rounds.
// Score = correct/10 × 100.  Higher = better.
const SYMBOLS = ['🔴','🟡','🟢','🔵','🟣','⬛','🔺','🔷','💠','🔶','♦️','🌀','⭐','🌸','🔮','💎'];
function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); }

function OddSymbolGame({ lang, onBack, allUsers, friends, currentUserId, userName, invitePartner }) {
  const nl = lang === 'nl';
  const TOTAL = 10;
  const [active, setActive]         = useState(false);
  const [finished, setFinished]     = useState(false);
  const [round, setRound]           = useState(0);
  const [score, setScore]           = useState(0);
  const [grid, setGrid]             = useState([]);
  const [oddIdx, setOddIdx]         = useState(-1);
  const [fb, setFb]                 = useState(null);
  const [timeLeft, setTimeLeft]     = useState(60);
  const [best, setBest]             = useStorage('gm_oddsymbol_best', 0);
  const [showInvite, setShowInvite] = useState(false);
  const [partner, setPartner]       = useState(null);
  const [showResults, setShowResults] = useState(null);

  // Auto-set partner when arriving from game room invite
  useEffect(() => {
    if (invitePartner) { setPartner(invitePartner); }
  }, [invitePartner]); // eslint-disable-line react-hooks/exhaustive-deps

  const timerRef  = useRef(null);
  const scoreRef  = useRef(0);
  const roundRef  = useRef(0);

  const makeGrid = () => {
    let base = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
    let odd  = base;
    while (odd === base) odd = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
    const cells = Array(16).fill(base);
    cells[Math.floor(Math.random() * 16)] = odd;
    const shuffled = shuffle(cells.map((sym, i) => ({ sym, isOdd: sym === odd && !cells.slice(0, i).includes(odd) })));
    // ensure exactly one odd
    let found = false;
    const fixed = shuffled.map(c => {
      if (c.sym === odd && !found) { found = true; return { sym: c.sym, isOdd: true }; }
      return { ...c, isOdd: false };
    });
    setGrid(fixed);
    setOddIdx(fixed.findIndex(c => c.isOdd));
    setFb(null);
  };

  const start = () => {
    scoreRef.current = 0; roundRef.current = 1;
    setScore(0); setRound(1); setTimeLeft(60);
    setActive(true); setFinished(false); setShowResults(null);
    makeGrid();
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); setActive(false); setFinished(true); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  useEffect(() => {
    if (finished) {
      const final = scoreRef.current * 10;
      if (final > best) setBest(final);
      saveGameScore('oddsymbol', final);
      if (partner) setShowResults({ myScore: final, partnerScore: Math.max(0, final + Math.floor(Math.random() * 20) - 10) });
    }
  }, [finished]); // eslint-disable-line

  const tap = (i) => {
    if (!active || fb !== null) return;
    const correct = i === oddIdx;
    if (correct) { scoreRef.current++; setScore(s => s + 1); }
    setFb(correct ? 'correct' : 'wrong');

    const nextRound = roundRef.current + 1;
    roundRef.current = nextRound;

    if (nextRound > TOTAL) {
      setTimeout(() => { clearInterval(timerRef.current); setActive(false); setFinished(true); }, 500);
    } else {
      setRound(nextRound);
      setTimeout(makeGrid, 520);
    }
  };

  if (showResults) return <MultiplayerResults lang={lang} myScore={showResults.myScore} myLabel="pts" partnerId={partner?.id} partnerScore={showResults.partnerScore} partnerLabel="pts" aiComment={nl ? `Score van ${showResults.myScore}/100! Odd Symbol traint visuele aandacht en patroonherkenning.` : `Score of ${showResults.myScore}/100! Odd Symbol trains visual attention and pattern recognition.`} onClose={() => { setShowResults(null); setPartner(null); }} onRematch={start} />;

  return (
    <div>
      {showInvite && <MultiplayerInviteModal lang={lang} title="Odd Symbol" icon="🔍" allUsers={allUsers} friends={friends} onInvite={(user) => { setPartner(user); setShowInvite(false); }} onClose={() => setShowInvite(false)} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}>← {nl ? 'Terug' : 'Back'}</button>
        <button onClick={() => setShowInvite(true)} style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, color: C.white, borderRadius: 10, padding: '5px 12px', cursor: 'pointer', fontSize: 11 }}>👥 {nl ? 'Samen' : 'Together'}</button>
      </div>
      {partner && <div style={{ ...s_GC({ padding: '8px 12px', marginBottom: 10 }), display: 'flex', alignItems: 'center', gap: 8 }}><Avatar user={partner} size={26} /><span style={{ fontSize: 12, color: C.teal }}>{partner.name} {nl ? 'speelt mee' : 'is playing'} 🟢</span></div>}

      {!active && !finished && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🔍</div>
          <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{nl ? 'Vind het vreemde symbool' : 'Find the Odd Symbol'}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 4, lineHeight: 1.6 }}>
            {nl ? `Één symbool in het raster is anders dan de rest. Tik het aan — ${TOTAL} rondes.` : `One symbol in the grid is different from the rest. Tap it — ${TOTAL} rounds.`}
          </div>
          {best > 0 && <div style={{ color: C.teal, fontSize: 13, margin: '12px 0' }}>🏆 Best: {best}/100</div>}
          <button style={s_PB({ marginTop: 8 })} onClick={start}>{nl ? 'Starten' : 'Start'}</button>
        </div>
      )}

      {active && grid.length > 0 && (
        <>
          <div style={{ ...s_GC({ padding: '10px 16px', marginBottom: 10 }), display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
            <div><div style={{ fontSize: 11, color: C.silver }}>{nl ? 'RONDE' : 'ROUND'}</div><div style={{ fontSize: 20, color: _T.accent, fontWeight: 700 }}>{round}/{TOTAL}</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>TIME</div><div style={{ fontSize: 20, color: timeLeft < 15 ? C.red : C.white, fontWeight: 700 }}>{timeLeft}s</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>✓</div><div style={{ fontSize: 20, color: C.teal, fontWeight: 700 }}>{score}</div></div>
          </div>
          <div style={{ ...s_GC({ padding: 12, marginBottom: 10 }) }}>
            {fb && <div className="popIn" style={{ textAlign: 'center', fontSize: 28, marginBottom: 8 }}>{fb === 'correct' ? '✅' : '❌'}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
              {grid.map((cell, i) => (
                <button key={i} onClick={() => tap(i)} style={{ padding: '10px 4px', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, cursor: 'pointer', fontSize: 24, textAlign: 'center' }}>
                  {cell.sym}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {finished && !showResults && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 34, color: _T.accent, fontWeight: 700, marginBottom: 4 }}>{score * 10}<span style={{ fontSize: 14, color: C.silver }}>/100</span></div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 12 }}>{score}/{TOTAL} {nl ? 'correct' : 'correct'}</div>
          <button style={s_PB()} onClick={start}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      )}
    </div>
  );
}

export default OddSymbolGame;
